import { Emitter } from "@core/events/emitter";
import type { ThreeEngine } from "@runtime/engine/engineService";
import type { OrbitCameraController } from "@runtime/camera/orbitCameraController";
import {
  isHeadsetBrowser,
  isSafariLikeBrowser,
} from "@runtime/browser/headsetDevice";
import { Quaternion, Vector3 } from "three";

/**
 * `XRSessionService` — WebXR session lifecycle for the editor viewport.
 *
 * Web-only surface (no Unity counterpart file): the Quest app boots directly
 * into OpenXR, while the web enters/exits an immersive session from the
 * desktop editor. Responsibilities:
 *
 *   - capability detection (`navigator.xr.isSessionSupported("immersive-vr")`),
 *   - session request/teardown around three's `renderer.xr`,
 *   - user spawn placement via an offset reference space (NOT a rig group:
 *     offsetting the reference space keeps `camera.position` world-correct,
 *     so every consumer that reads the camera — billboards, Spark head,
 *     multiuser pose publishing, constant-size gizmos — works unchanged
 *     in-session),
 *   - suppressing the desktop orbit controller for the duration and
 *     restoring its exact pose on exit.
 *
 * Registered headless in the core IOC pass (SceneStatisticsService pattern);
 * `ViewportView` hands it the live engine + camera via `attachViewport` and
 * detaches on unmount. Inert without a viewport.
 *
 * **Project entry IS VR entry on a headset** (user direction 2026-08-06): the
 * projects list stays a normal 2D browser page; opening a project starts the
 * immersive session. Browsers only allow `requestSession` inside a USER
 * GESTURE, and the editor (and its GL context) doesn't exist until after the
 * route change — so the card click calls
 * {@link requestSessionForProjectEntryAsync} (gesture-valid, no renderer
 * needed), the session is held PENDING, and `attachViewport` adopts it when
 * the editor mounts. A pending session nothing adopts is discarded after a
 * timeout so a failed navigation can't leave the headset in a dead black
 * session.
 *
 * Input (hands / gaze) is owned by `XRInputController`; panels by
 * `XRPanelsRenderer`. Both key off `onSessionStateChanged`.
 */

export type XRAvailability = "unsupported" | "insecure-context" | "available";

/**
 * Where the user appears at session start, in world meters. Scene content is
 * real-scale on the table at the origin (table top y≈0.343 — see
 * `sceneEnvironmentRenderer.ts`), and `local-floor` puts the user's feet at
 * y=0, so the only placement needed is standing back from the table center.
 * `local-floor`'s -Z axis follows the user's initial facing, which the offset
 * preserves — so they start looking at the table.
 *
 * ⚠ **Derive this from the table's EDGE — never pick a bare number.** The
 * distance is measured to the scene ORIGIN, which is the table's CENTRE, and
 * `public/environment/table.glb` spans ±1.04 m in X/Z and is added UNSCALED —
 * a 2.08 m wide table. So the literal "1 m from the scene" asked for on
 * 2026-08-10 put the user 4 cm from the table's near edge, standing in the
 * furniture; and the 1.4 m this originally shipped with left only 36 cm, which
 * is why the scene read as too close both times. What a person means by "the
 * scene is N metres away" is the near EDGE, so that is what is expressed here.
 * Keep the half-extent in step if `table.glb` is ever re-exported or scaled.
 */
export const K_TABLE_HALF_EXTENT_M = 1.04;
/** Clear floor between the user and the table's near edge (user direction). */
const K_TABLE_CLEARANCE_M = 1.0;
const K_SPAWN_DISTANCE_M = K_TABLE_HALF_EXTENT_M + K_TABLE_CLEARANCE_M;

const K_SESSION_FEATURES: XRSessionInit = {
  // Everything optional: `local-floor` is granted on Quest + Vision Pro but
  // must not hard-fail elsewhere; `hand-tracking` unlocks joints on Quest and
  // (visionOS 26+) Safari — Vision Pro's gaze+pinch `transient-pointer`
  // input needs no feature at all. Never move these to requiredFeatures.
  optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking", "layers"],
};

/** How long a project-entry session may wait for the editor to mount. */
const K_PENDING_ADOPT_TIMEOUT_MS = 30_000;

export class XRSessionService {
  readonly onAvailabilityChanged = new Emitter<XRAvailability>();
  readonly onSessionStateChanged = new Emitter<boolean>();
  /**
   * The headset recentered — what a wearer calls a **position reset** (Quest:
   * hold the Meta button; Vision Pro: hold the Digital Crown). WebXR reports
   * it as a `reset` event on the reference space, and it is the web's
   * counterpart of `OVRManager.display.RecenteredPose`, which the Quest app
   * uses to re-place its Universal Canvas
   * (`UniversalMenuPlacementModel.OnRecenteredPose`).
   *
   * The user themselves needs no handling: the spawn offset is expressed
   * against the reference space's native origin, so it survives the reset and
   * puts them back exactly `K_SPAWN_DISTANCE_M` from the table facing it. The
   * floating slates are the part that needs doing — they are world-locked, so
   * a recenter leaves them wherever the wearer was standing when they opened
   * them (`XRPanelsRenderer` re-places them from this).
   */
  readonly onReferenceSpaceReset = new Emitter<void>();

  private m_engine: ThreeEngine | null = null;
  private m_camera: OrbitCameraController | null = null;
  private m_session: XRSession | null = null;
  private m_availability: XRAvailability = "unsupported";
  /**
   * Platform-split session mode (user direction 2026-08-06): Safari/Vision
   * Pro gets `immersive-vr` with the full environment; every other XR
   * browser (Quest) prefers `immersive-ar` passthrough. The Safari check is
   * load-bearing: visionOS's AR module is a non-functional flag, yet
   * `isSessionSupported("immersive-ar")` can answer TRUE there — preferring
   * AR then yields a broken black session and poisons every
   * blend-mode-derived decision downstream (learned across three on-device
   * rounds). Resolved during availability detection so request paths stay
   * synchronous with their user gesture.
   */
  private m_preferredMode: XRSessionMode = "immersive-vr";
  private m_savedPose: { position: Vector3; target: Vector3 } | null = null;
  private m_entering = false;
  /** Granted-but-unattached session from a project-card click (see header). */
  private m_pendingSession: XRSession | null = null;
  private m_pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  /** True while a project-entry `requestSession` sits in the permission prompt. */
  private m_requestingProjectEntry = false;
  /** Detach fns for the `reset` listeners on this session's reference spaces. */
  private readonly m_referenceSpaceResetOffs: Array<() => void> = [];
  private readonly m_headPosition = new Vector3();
  private readonly m_headQuaternion = new Quaternion();

  constructor() {
    void this.detectAvailability();
    // Headsets can connect after page load (Quest Link, simulator extensions).
    (navigator.xr as unknown as EventTarget | undefined)?.addEventListener?.(
      "devicechange",
      () => void this.detectAvailability(),
    );
  }

  get availability(): XRAvailability {
    return this.m_availability;
  }

  get isPresenting(): boolean {
    return this.m_session !== null;
  }

  get session(): XRSession | null {
    return this.m_session;
  }

  /**
   * What the browser actually GRANTED, not what was requested: passthrough
   * composites the real room (`immersive-ar` — Quest browser), immersive is
   * an opaque VR world (Vision Pro Safari — its WebXR AR module is a
   * non-functional flag as of visionOS 26). Presentation decisions (sky,
   * hand meshes) branch on this, never on the requested mode.
   */
  get presentationMode(): "passthrough" | "immersive" | null {
    if (!this.m_session) return null;
    // STRICT match: only the two blend modes that genuinely composite the
    // real world count as passthrough. An absent/unknown value (visionOS
    // Safari has reported unreliable values around its dead AR flag) must
    // read as an opaque world — misclassifying as passthrough blanks the
    // sky and hides the stage furniture in a session that can't composite.
    const blend = this.m_session.environmentBlendMode;
    return blend === "alpha-blend" || blend === "additive" ? "passthrough" : "immersive";
  }

  /** The live engine while a viewport is attached (input/panel layers use it). */
  get engine(): ThreeEngine | null {
    return this.m_engine;
  }

  attachViewport(engine: ThreeEngine, camera: OrbitCameraController): void {
    this.m_engine = engine;
    this.m_camera = camera;
    // A project-entry session may already be waiting for this mount.
    if (this.m_pendingSession) void this.adoptPendingSessionAsync();
  }

  detachViewport(): void {
    // The GL context is going away — the session cannot outlive it.
    if (this.m_session) void this.m_session.end().catch(() => undefined);
    this.discardPendingSession();
    this.m_engine = null;
    this.m_camera = null;
  }

  async enterAsync(): Promise<void> {
    // `m_requestingProjectEntry`: a project-entry request may be sitting in
    // the browser's permission prompt — a second `requestSession` while one
    // is pending rejects with InvalidStateError, and the pending session
    // will be adopted on grant anyway, which is what the caller wanted.
    if (this.m_session || this.m_entering || this.m_requestingProjectEntry) return;
    const engine = this.m_engine;
    const camera = this.m_camera;
    const xr = navigator.xr;
    if (!engine || !camera || !xr) {
      throw new Error("XRSessionService: no viewport attached or WebXR unavailable");
    }

    this.m_entering = true;
    try {
      // Reuse a not-yet-adopted project-entry session over requesting a new
      // one (a second request would double-prompt the user).
      const session =
        this.takePendingSession() ??
        (await xr.requestSession(this.m_preferredMode, K_SESSION_FEATURES));
      await this.activateSessionAsync(session);
      this.onSessionStateChanged.emit(true);
    } finally {
      this.m_entering = false;
    }
  }

  /**
   * Request an immersive session from a USER GESTURE before the editor / GL
   * context exists — the project-card click on a headset browser. The
   * session is held pending and adopted by `attachViewport` when the editor
   * mounts; discarded on timeout, on viewport detach, or when the user ends
   * it from the system UI first. No-op when immersive-vr is unsupported, so
   * the projects page needs no capability branching.
   */
  async requestSessionForProjectEntryAsync(): Promise<void> {
    if (this.m_session || this.m_pendingSession || this.m_entering) return;
    if (this.m_requestingProjectEntry) return;
    if (this.m_availability !== "available" || !navigator.xr) return;
    // AUTO-entry is headset-browser-only: `isSessionSupported` is also true
    // on a desktop Chrome that merely has an XR runtime attached (Quest
    // Link, SteamVR, the WebXR emulator), and yanking that user into VR on
    // every project click is hostile. The header pill still offers manual
    // entry there.
    if (!this.isHeadsetBrowser) return;
    this.m_requestingProjectEntry = true;
    try {
      const session = await navigator.xr.requestSession(this.m_preferredMode, K_SESSION_FEATURES);
      this.m_pendingSession = session;
      session.addEventListener("end", this.handlePendingSessionEnded);
      this.m_pendingTimeout = setTimeout(
        () => this.discardPendingSession(),
        K_PENDING_ADOPT_TIMEOUT_MS,
      );
      // The editor may have mounted while the permission prompt was up.
      if (this.m_engine && this.m_camera) void this.adoptPendingSessionAsync();
    } catch (err) {
      console.warn("[XRSessionService] project-entry XR session request failed", err);
    } finally {
      this.m_requestingProjectEntry = false;
    }
  }

  /**
   * True on browsers that RUN ON a headset (visionOS Safari, Quest
   * Browser) — the devices where project entry should auto-enter XR.
   * Desktop browsers with an attached runtime report XR support but keep
   * manual entry only.
   *
   * ⚠ THE `navigator.xr` TERM IS LOAD-BEARING, NOT A CHEAP GUARD.
   * `isSafariLikeBrowser()` cannot tell visionOS Safari from iPhone, iPad or
   * macOS Safari — Apple deliberately gives visionOS a macOS user agent, which
   * is why the session-mode decision leans on it and comments that "any Safari
   * with working immersive WebXR is visionOS in practice". That reasoning holds
   * only where a SESSION is already possible; this getter is also used as a
   * plain DEVICE test where none is (`ProjectsView.enterProject`, and the
   * workflow autostart's desktop-vs-headset probe), and there the UA alone said
   * every Safari was a headset. The visible cost was on iPhone: project entry
   * took the headset short-circuit, which navigates IMMEDIATELY and so skipped
   * the load overlay that is supposed to cover the grid — "when i click project
   * to open - i briefly see project list before project starts opening".
   *
   * iOS and macOS Safari expose no `navigator.xr` at all, while visionOS Safari
   * and Quest Browser do, so this is the discriminator the UA cannot be. It
   * also makes the sentence above true rather than aspirational.
   */
  get isHeadsetBrowser(): boolean {
    return isHeadsetBrowser();
  }

  private async adoptPendingSessionAsync(): Promise<void> {
    if (this.m_session || this.m_entering) {
      this.discardPendingSession();
      return;
    }
    const session = this.takePendingSession();
    if (!session) return;
    this.m_entering = true;
    try {
      await this.activateSessionAsync(session);
      this.onSessionStateChanged.emit(true);
    } catch (err) {
      console.warn("[XRSessionService] adopting the project-entry session failed", err);
    } finally {
      this.m_entering = false;
    }
  }

  /** Attach a live (already granted) session to the renderer + camera. */
  private async activateSessionAsync(session: XRSession): Promise<void> {
    const engine = this.m_engine;
    const camera = this.m_camera;
    if (!engine || !camera) {
      await session.end().catch(() => undefined);
      throw new Error("XRSessionService: no viewport attached");
    }
    this.m_session = session;
    session.addEventListener("end", this.handleSessionEnded);
    // Freeze the desktop camera: WebXR overwrites the camera pose every
    // frame, and the orbit controller must not fight it (nor leave the
    // user's desktop framing destroyed on exit).
    this.m_savedPose = camera.getPose();
    camera.setInteractionSuppressed(true);

    const renderer = engine.renderer;
    try {
      renderer.xr.enabled = true;
      // Probe the reference space DIRECTLY (side-effect-free) rather than
      // letting `setSession` fail half-configured on a refused
      // `local-floor` and retrying a session with dangling listeners.
      renderer.xr.setReferenceSpaceType(await resolveReferenceSpaceType(session));
      await renderer.xr.setSession(session);
      // Always-on breadcrumb: on-device triage happens through the remote
      // Web Inspector, and "why is there no passthrough" is unanswerable
      // without knowing what the UA actually granted.
      console.info(
        `[XRSessionService] session started (blend=${session.environmentBlendMode}, ` +
          `preferred=${this.m_preferredMode})`,
      );

      // Spawn placement. `getOffsetReferenceSpace(T)` re-expresses poses as
      // `T⁻¹ · pose`, so placing the viewer at world P takes T = -P (same
      // sign convention as three's teleport example).
      const base = renderer.xr.getReferenceSpace();
      if (base) {
        const spawned = base.getOffsetReferenceSpace(
          new XRRigidTransform({ x: 0, y: 0, z: -K_SPAWN_DISTANCE_M }),
        );
        renderer.xr.setReferenceSpace(spawned);
        // BOTH spaces are watched for the recenter. `spawned` is the one three
        // renders from, but whether a UA fires `reset` on a space derived by
        // `getOffsetReferenceSpace` is not something to bet the behaviour on —
        // a missed event strands every slate behind the wearer, while a double
        // fire costs nothing (re-placement is idempotent).
        this.watchReferenceSpaceReset(base);
        this.watchReferenceSpaceReset(spawned);
      }
    } catch (err) {
      // Detach our handler BEFORE ending so the failed attempt doesn't run
      // the exit path, then release the UA session (otherwise the headset
      // keeps presenting a dead session).
      session.removeEventListener("end", this.handleSessionEnded);
      this.m_session = null;
      this.clearReferenceSpaceResetWatch();
      renderer.xr.enabled = false;
      this.restoreDesktopCamera();
      await session.end().catch(() => undefined);
      // three's own end handler "restores" a pixel ratio / drawing-buffer
      // size it never captured when `setSession` failed before its capture
      // point (`makeXRCompatible`) — that restore computes a 0×0 canvas.
      // Re-derive the desktop size the way the normal exit path does; our
      // end handler was detached above, so it won't run this heal itself.
      this.scheduleDesktopSizeHeal();
      throw err;
    }
  }

  /** Subscribe one reference space to the recenter, remembering the detach. */
  private watchReferenceSpaceReset(space: XRReferenceSpace): void {
    const handler = () => this.onReferenceSpaceReset.emit();
    space.addEventListener("reset", handler);
    this.m_referenceSpaceResetOffs.push(() => space.removeEventListener("reset", handler));
  }

  private clearReferenceSpaceResetWatch(): void {
    for (const off of this.m_referenceSpaceResetOffs) off();
    this.m_referenceSpaceResetOffs.length = 0;
  }

  /**
   * Re-run the engine's resize pass AFTER three's session-end restore (ours
   * fires first — attached before `setSession` added theirs), covering both
   * mid-session window resizes and three restoring sizes it never captured.
   */
  private scheduleDesktopSizeHeal(): void {
    setTimeout(() => {
      if (this.m_engine) window.dispatchEvent(new Event("resize"));
    }, 0);
  }

  /** Detach and return the pending session, if any (caller takes ownership). */
  private takePendingSession(): XRSession | null {
    const session = this.m_pendingSession;
    if (!session) return null;
    this.clearPendingTimer();
    session.removeEventListener("end", this.handlePendingSessionEnded);
    this.m_pendingSession = null;
    return session;
  }

  private discardPendingSession(): void {
    const session = this.takePendingSession();
    if (session) void session.end().catch(() => undefined);
  }

  private clearPendingTimer(): void {
    if (this.m_pendingTimeout !== null) {
      clearTimeout(this.m_pendingTimeout);
      this.m_pendingTimeout = null;
    }
  }

  /** The user ended the pending session (system gesture) before adoption. */
  private readonly handlePendingSessionEnded = (): void => {
    this.clearPendingTimer();
    this.m_pendingSession = null;
  };

  async exitAsync(): Promise<void> {
    const session = this.m_session;
    if (!session) return;
    await session.end().catch(() => undefined);
    // Cleanup happens in handleSessionEnded (also covers system-initiated
    // exits — Quest home button, Vision Pro Digital Crown).
  }

  /**
   * Head pose in world space while presenting (reference-space offset means
   * the user camera's transform IS world-space). Reused scratch objects —
   * copy if retaining.
   */
  getHeadWorldPose(): { position: Vector3; quaternion: Quaternion } | null {
    const engine = this.m_engine;
    const camera = this.m_camera;
    if (!engine || !camera || !this.m_session) return null;
    camera.camera.getWorldPosition(this.m_headPosition);
    camera.camera.getWorldQuaternion(this.m_headQuaternion);
    return { position: this.m_headPosition, quaternion: this.m_headQuaternion };
  }

  private readonly handleSessionEnded = (): void => {
    this.m_session?.removeEventListener("end", this.handleSessionEnded);
    this.m_session = null;
    this.clearReferenceSpaceResetWatch();
    if (this.m_engine) this.m_engine.renderer.xr.enabled = false;
    this.restoreDesktopCamera();
    // The UA can end a session WHILE activation is still awaiting inside
    // `activateSessionAsync` — `onSessionStateChanged.emit(true)` never
    // fired for it, so the exit emit must not fire either (subscribers
    // would tear down state they never set up). The cleanup above is
    // idempotent; the in-flight activation's catch finishes the rest when
    // its pending await rejects.
    if (this.m_entering) return;
    this.onSessionStateChanged.emit(false);
    // The engine's resize handler no-ops while presenting, so a window
    // resize that happened mid-session left the canvas stale.
    this.scheduleDesktopSizeHeal();
  };

  private restoreDesktopCamera(): void {
    const camera = this.m_camera;
    if (!camera) return;
    camera.setInteractionSuppressed(false);
    if (this.m_savedPose) {
      camera.setPose(this.m_savedPose.position, this.m_savedPose.target);
      this.m_savedPose = null;
    }
  }

  private async detectAvailability(): Promise<void> {
    let next: XRAvailability = "unsupported";
    // **The BROWSER is the authority, not the spec text.** `navigator.xr` is
    // [SecureContext], so on `http://<lan-ip>` it is usually undefined
    // entirely — but "usually" is doing real work there: Quest's browser hands
    // it out on a plain-http LAN origin and happily presents, which is how
    // by-IP dev testing on Quest worked before this probe checked
    // `isSecureContext` FIRST and started reporting `insecure-context` for
    // everyone (user report 2026-08-10: "webxr scene was working before when I
    // ran it on headset over local ip… now I see https warning in ui").
    //
    // So: if the API is here, ASK IT. Only when it is absent do we fall back
    // to explaining why, and an insecure context is by far the likeliest
    // reason — that is the honest diagnostic the check was added for
    // (Vision Pro over http, where it genuinely does vanish).
    if (navigator.xr) {
      const supports = async (mode: XRSessionMode) => {
        try {
          return await navigator.xr!.isSessionSupported(mode);
        } catch {
          return false;
        }
      };
      const vr = await supports("immersive-vr");
      const ar = !isSafariLikeBrowser() && (await supports("immersive-ar"));
      this.m_preferredMode = ar ? "immersive-ar" : "immersive-vr";
      next = vr || ar ? "available" : "unsupported";
    } else if (!window.isSecureContext) {
      next = "insecure-context";
    }
    if (next !== this.m_availability) {
      this.m_availability = next;
      this.onAvailabilityChanged.emit(next);
    }
  }
}

/**
 * Probe which reference space the session actually granted. Calling
 * `requestReferenceSpace` directly has no renderer side effects, unlike
 * finding out via a failing `renderer.xr.setSession`. `local-floor` is
 * granted on Quest + Vision Pro; `local` (seated, origin at the head) is the
 * spec-guaranteed fallback.
 */
async function resolveReferenceSpaceType(session: XRSession): Promise<XRReferenceSpaceType> {
  try {
    await session.requestReferenceSpace("local-floor");
    return "local-floor";
  } catch {
    return "local";
  }
}

export const XR_SESSION_SERVICE = Symbol.for("XRSessionService");
